/**
 * Device Fingerprinting Service using FingerprintJS
 * Provides robust device identification and mobile detection
 * that cannot be easily bypassed by changing User-Agent alone
 */

import FingerprintJS from '@fingerprintjs/fingerprintjs'

let fpPromise = null
let cachedFingerprint = null

/**
 * Initialize FingerprintJS
 */
const initFingerprint = async () => {
  if (!fpPromise) {
    fpPromise = FingerprintJS.load()
  }
  return fpPromise
}

/**
 * Detect if the device is mobile using multiple signals
 * @returns {boolean}
 */
const isMobileDevice = () => {
  // Check 1: User-Agent
  const userAgent = navigator.userAgent || navigator.vendor || window.opera
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i
  const isMobileUA = mobileRegex.test(userAgent.toLowerCase())

  // Check 2: Screen size (mobile typically < 768px width)
  const isMobileScreen = window.screen.width < 768 || window.innerWidth < 768

  // Check 3: Touch capability
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  // Check 4: Orientation API (primarily available on mobile)
  const hasOrientation = typeof window.orientation !== 'undefined'

  // Check 5: Pointer type (coarse = touch/finger, typically mobile)
  let hasCoarsePointer = false
  if (window.matchMedia) {
    hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches
  }

  // Check 6: Mobile-specific platform
  const platform = navigator.platform || ''
  const isMobilePlatform = /iPhone|iPad|iPod|Android|Linux armv/i.test(platform)

  // Device is considered mobile if multiple indicators suggest so
  // Using a scoring system for higher accuracy
  let mobileScore = 0
  if (isMobileUA) mobileScore += 3 // High weight
  if (isMobileScreen) mobileScore += 2
  if (hasTouchScreen && hasCoarsePointer) mobileScore += 2 // Only count if both present
  if (hasOrientation) mobileScore += 1
  if (isMobilePlatform) mobileScore += 2

  // Threshold: Score >= 4 means likely mobile
  return mobileScore >= 4
}

/**
 * Get comprehensive device fingerprint with mobile detection
 * @returns {Promise<Object>}
 */
export const getDeviceFingerprint = async () => {
  try {
    // Return cached fingerprint if available (cache for 5 minutes)
    if (cachedFingerprint && Date.now() - cachedFingerprint.timestamp < 300000) {
      return cachedFingerprint.data
    }

    const fp = await initFingerprint()
    const result = await fp.get()

    const deviceInfo = {
      // Unique visitor ID
      visitorId: result.visitorId,

      // Mobile detection
      isMobile: isMobileDevice(),

      // Device characteristics
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio || 1,
      touchSupport: 'ontouchstart' in window,
      maxTouchPoints: navigator.maxTouchPoints || 0,

      // Browser info
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      languages: navigator.languages,

      // Hardware concurrency (CPU cores)
      hardwareConcurrency: navigator.hardwareConcurrency,

      // Timestamp
      timestamp: Date.now()
    }

    // Cache the result
    cachedFingerprint = {
      data: deviceInfo,
      timestamp: Date.now()
    }

    return deviceInfo
  } catch (error) {
    console.error('Error generating device fingerprint:', error)

    // Fallback: basic mobile detection if FingerprintJS fails
    return {
      visitorId: 'unknown',
      isMobile: isMobileDevice(),
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      timestamp: Date.now(),
      error: true
    }
  }
}

/**
 * Clear cached fingerprint (useful for testing or manual refresh)
 */
export const clearFingerprintCache = () => {
  cachedFingerprint = null
}

/**
 * Quick mobile check without full fingerprinting (faster)
 * @returns {boolean}
 */
export const quickMobileCheck = () => {
  return isMobileDevice()
}

export default {
  getDeviceFingerprint,
  clearFingerprintCache,
  quickMobileCheck
}
